import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { SlideUpService } from '../../../services/slide-up.service';
import { CommentService } from '../../../services/comment.service';
import { PaginationData } from '../../components/feed-paginator/pagination-data.interface';
import { Comment, Commentable } from '../comment.interface';

@Component({
  selector: 'ut-comment-section',
  templateUrl: './comment-section.component.html',
  styleUrls: ['./comment-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommentSectionComponent implements OnInit, OnDestroy {
  @Input() service: any;
  @Input() commentable!: Commentable;
  @Input() commentBtnLabel: string = 'Write a Comment';

  comments: Comment[] = [];
  paginationData: PaginationData = {
    nextPage: null,
    previousPage: null,
    totalItems: 0,
    perPage: 10,
    totalPages: 0,
    currentPage: 1,
    lastPage: null,
    firstPage: null,
    paginationNumbers: [],
  };

  isLoading = false;
  currentUser: any = null;
  loginUrl = '';
  newComment: any = { content: '' };
  writeCommentOpened = false;
  replyFocusIndex: number | null = null;
  selectedComment: string = '';
  wrapperClass = '';
  sorting = '';
  sortingOptions: { [key: string]: string } = {};
  search = {
    query: '',
    submit: () => this.handleSearchSubmit(),
    clear: () => this.handleSearchClear(),
  };

  deletingCommentId: number | null = null;
  currentCommentKey: string | null = null;
  mode = '';
  emojiImages: any;
  optionKeys: string[] = [];

  private destroy$ = new Subject<void>();
  private indentations: { [key: number]: number } = {};

  constructor(
    private cdr: ChangeDetectorRef,
    private slideUpService: SlideUpService,
    private sanitizer: DomSanitizer,
    @Inject('$location') private $location: any,
    @Inject('$filter') private $filter: any,
    @Inject('$state') private $state: any,
    @Inject('$stateParams') private $stateParams: any,
    @Inject('PaginatorService') private PaginatorService: any,
    @Inject('stateStore') private stateStore: any,
    private CommentService: CommentService,
    @Inject('AlertService') private AlertService: any,
    @Inject('UserService') private UserService: any,
    @Inject('ProgressBarService') private ProgressBarService: any,
    @Inject('ServerMessages') private ServerMessages: any,
    @Inject('LightTesterErrorService') private LightTesterErrorService: any,
    @Inject('commentEmojis') commentEmojis: any,
  ) {
    this.emojiImages = commentEmojis.emojis;
  }

  ngOnInit(): void {
    this.selectedComment = this.$location.hash().toString().split('_')[1];

    // Load current user
    this.UserService.me().then((res: any) => {
      if (res !== null) {
        this.currentUser = res.data;
      } else {
        this.currentUser = null;
        this.loginUrl = `/login?return_to=${encodeURIComponent(this.$location.absUrl())}`;
      }
      this.cdr.markForCheck();
    });

    // Setup sorting based on commentable type
    if (this.commentable.type === 'topic') {
      this.sorting = this.$location.search().sort || 'oldest';
      this.sortingOptions = {
        oldest: 'Oldest first',
        newest: 'Newest first',
      };
    } else {
      this.sorting = this.$location.search().sort || '-threaded';
      this.sortingOptions = {
        '-threaded': 'Newest first',
        threaded: 'Oldest first',
        liked_threaded: 'Popular first',
      };
    }

    // Setup wrapper class
    this.wrapperClass = this.commentable.type;
    if (this.commentable.type !== 'topic') {
      this.wrapperClass += ' section-single-comment-dark';
    }

    // Load initial page
    const getParamName = this.commentable.type === 'topic' ? 'page' : 'comments';
    const getPageNumber = this.commentable.type === 'topic'
      ? this.$stateParams[getParamName]
      : parseInt(this.$location.search()[getParamName]);
    const initialPage = !getPageNumber ? 1 : getPageNumber;
    this.loadPage(initialPage, true);
  }

  loadPage = (page: number, initialLoad = false, forceUrlChange = false): void => {
    this.isLoading = true;
    this.cdr.markForCheck();

    const pageFuncName = this.commentable.type === 'topic' ? 'getPage' : 'commentsPage';
    const id = !this.commentable.slug ? this.commentable.id : this.commentable.slug;

    this.service[pageFuncName](id, page, this.sorting, this.search.query).then(
      (res: any) => {
        this.comments = res.data.map((o: Comment) => {
          const markdownHtml = this.$filter('uMarkdown')(o.content);
          o.content_built = this.sanitizer.bypassSecurityTrustHtml(markdownHtml);
          return this.CommentService.addIndentation(o, this.indentations);
        });
        this.paginationData = this.PaginatorService.makePaginationData(res.headers);
        this.isLoading = false;
        this.cdr.markForCheck();

        // Handle scrolling
        if (initialLoad) {
          setTimeout(() => {
            const el = document.getElementById(this.$location.hash());
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        } else {
          setTimeout(() => {
            const comment = document.getElementById('comment');
            if (comment) {
              const containers = document.querySelectorAll('[ui-view$="-show"]');
              containers.forEach((container) => {
                if (container.contains(comment)) {
                  (container as HTMLElement).scrollTop = comment.offsetTop - 50;
                }
              });
            }
            if (this.replyFocusIndex != null) {
              const replyEl = document.getElementById(`reply_${this.replyFocusIndex}`);
              if (replyEl) {
                replyEl.focus();
              }
            }
            this.replyFocusIndex = null;
          }, 100);
        }

        if (this.commentable.comments) {
          this.commentable.comments.count = this.paginationData.totalItems;
        }

        // Update URL
        if ((this.paginationData && this.paginationData.currentPage !== page && !this.search.query) || forceUrlChange) {
          const getParamName = this.commentable.type === 'topic' ? 'page' : 'comments';
          let itemState: any;
          if (this.commentable.type === 'topic') {
            itemState = { state: 'singlediscussionpage', params: { discussionId: this.commentable.id } };
          } else {
            itemState = this.stateStore.getState(this.commentable);
          }
          const params: any = {};
          params[getParamName] = page;
          if (this.sorting) {
            params.sort = this.sorting;
          }
          const stateGoOptions: any = { notify: false, location: 'replace' };
          this.$state.go(itemState.state, { ...itemState.params, ...params }, stateGoOptions);
        }
      },
      (res: any) => {
        this.isLoading = false;
        this.AlertService.addDanger(res.statusText);
        this.cdr.markForCheck();
      },
    );
  };

  sortingChanged = (val: string): void => {
    this.sorting = val;
    if (!this.isLoading) {
      this.loadPage(1, false, true);
    }
  };

  handleSearchSubmit(): void {
    if (!this.isLoading) {
      this.loadPage(1);
    }
  }

  handleSearchClear(): void {
    this.search.query = '';
  }

  setComment(comment: any): void {
    this.newComment = comment;
  }

  setQuote(comment: Comment): void {
    this.newComment = {
      content: `> @${comment.user.slug} wrote: \n> ${comment.content.split('\n').join('\n> ')}\n\n`,
    };
  }

  deleteComment(id: number): void {
    this.CommentService.deleteComment(id).then(() => {
      this.deletingCommentId = null;
      if (this.comments.length > 1 || this.paginationData.currentPage === 1) {
        this.loadPage(this.paginationData.currentPage);
      } else {
        this.loadPage(this.paginationData.currentPage - 1);
      }
      this.AlertService.add('Comment successfully deleted');
    });
  }

  openCommentEditor(comment: any = { content: '' }, type = 'comment'): boolean {
    if (this.LightTesterErrorService.show()) return false;

    // Generate a unique key for this comment to determine if it's the same as what's currently being edited
    const newCommentKey = this.getCommentKey(comment, type);
    const isOpen = this.slideUpService.isOpen('singleCommentSection');
    const isSameComment = this.currentCommentKey === newCommentKey;

    if (isOpen && isSameComment) {
      // Same comment is already being edited - toggle closed
      this.slideUpService.close('singleCommentSection');
      this.currentCommentKey = null;
    } else {
      // Different comment or panel is closed - set content and ensure panel is open
      if (type === 'comment') {
        this.setComment(comment);
      } else if (type === 'quote') {
        this.setQuote(comment);
      }
      this.slideUpService.open('singleCommentSection');
      this.currentCommentKey = newCommentKey;
    }

    return true;
  }

  private getCommentKey(comment: any, type: string): string {
    // Generate a unique key for a comment based on its identity
    if (comment.id) {
      // Editing an existing comment
      return `edit-${comment.id}`;
    }
    if (comment.parent_id) {
      // Reply to a specific comment
      return `reply-${comment.parent_id}`;
    }
    if (type === 'quote' && comment.content) {
      // Quote reply - use a hash of the content
      return `quote-${comment.content.substring(0, 50)}`;
    }
    // New top-level comment
    return 'new-comment';
  }

  broadcastOpening(): void {
    // Open the Angular slide-up panel
    this.slideUpService.open('singleCommentSection');
  }

  closeCommentForm(): void {
    this.slideUpService.close('singleCommentSection');
    this.currentCommentKey = null;
  }

  submitComment(): void {
    const trimmedComment = this.newComment.content?.trim();
    const EMPTY_CONTENT_MARKDOWNS = ['> \n\n'];
    if (!trimmedComment || EMPTY_CONTENT_MARKDOWNS.includes(trimmedComment)) {
      this.AlertService.addDanger("Can't post empty comment!");
      return;
    }

    this.ProgressBarService.start();
    if (!this.newComment.id) {
      const replyIndex = this.newComment.reply_index;
      delete this.newComment.reply_index;
      this.CommentService.postComment(this.commentable.type, this.commentable.id, this.newComment).then(
        (res: any) => {
          if (!res.data.parent_id) {
            if (this.sorting === 'newest' || this.sorting === '-threaded') {
              this.loadPage(1);
            } else {
              const lastPage = Math.ceil((this.paginationData.totalItems + 1) / this.paginationData.perPage);
              this.loadPage(lastPage, lastPage === this.paginationData.currentPage);
            }
          } else if (replyIndex === 9) {
            this.loadPage(this.paginationData.currentPage + 1);
          } else {
            this.loadPage(this.paginationData.currentPage);
          }

          this.AlertService.add('Comment successfully posted');
          // Close the slide-up form after successful submission
          this.slideUpService.close('singleCommentSection');
          this.currentCommentKey = null;
          this.ProgressBarService.done();
        },
        (response: any) => {
          this.AlertService.addDanger(this.ServerMessages.prettyMessage(response));
          this.ProgressBarService.done();
        },
      );
    } else {
      this.CommentService.editComment(this.newComment.id, this.newComment).then(() => {
        // Close the slide-up form after successful edit
        this.slideUpService.close('singleCommentSection');
        this.currentCommentKey = null;
        this.AlertService.add('Comment successfully changed');
        this.loadPage(this.paginationData.currentPage);
        this.ProgressBarService.done();
      });
    }
  }

  focusBtn(triggerBtn: string): void {
    switch (triggerBtn) {
      case 'delete': {
        const btnNo = document.getElementById('deleteCommentBtnNo');
        if (btnNo) {
          btnNo.setAttribute('aria-hidden', 'false');
          setTimeout(() => {
            const deleteBtn = document.getElementById('deleteCommentBtn');
            if (deleteBtn) deleteBtn.setAttribute('aria-hidden', 'true');
            btnNo.focus();
          }, 150);
        }
        break;
      }
      case 'no': {
        const deleteBtn = document.getElementById('deleteCommentBtn');
        if (deleteBtn) {
          deleteBtn.setAttribute('aria-hidden', 'false');
          setTimeout(() => {
            const btnNo = document.getElementById('deleteCommentBtnNo');
            if (btnNo) btnNo.setAttribute('aria-hidden', 'true');
            deleteBtn.focus();
          }, 150);
        }
        break;
      }
      default:
        break;
    }
  }

  handleKeydownDeleteConformation(event: KeyboardEvent): void {
    const { id } = event.target as HTMLElement;
    if (!event.shiftKey && event.keyCode === 9 && id === 'deleteCommentBtnYes') {
      event.preventDefault();
      const btnNo = document.getElementById('deleteCommentBtnNo');
      if (btnNo) btnNo.focus();
    } else if (event.shiftKey && event.keyCode === 9 && id === 'deleteCommentBtnNo') {
      event.preventDefault();
      const btnYes = document.getElementById('deleteCommentBtnYes');
      if (btnYes) btnYes.focus();
    }
  }

  selectEmoji(emojiCode: string): void {
    this.setMode('');
    let space = '';
    if (!this.newComment.content) {
      this.newComment.content = '';
    } else {
      space = this.newComment.content.slice(-1) === ' ' ? '' : ' ';
    }
    this.newComment.content += space + emojiCode;
  }

  setMode(mode: string): void {
    this.mode = this.mode === mode ? '' : mode;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
